import { AfterViewInit, Component, HostBinding, NgZone, OnDestroy, OnInit } from '@angular/core';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { extractColors } from 'extract-colors';
import { THEMES } from '../../../../data/product/product.data';
import { DYNAMIC_WALLPAPER_ABOUT } from '../../../../data/tool/tool-about.data';
import { URLS } from '../../../../data/navigation/navigation.data';
import { HEX_CODES } from '../../../../data/color/color.data';
import { ColorService } from '../../../../service/color/color.service';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { PaletteGenerationService } from '../../../../service/palette/palette-generation.service';
import { PaletteSubtitle } from '../../../../component/palette-widget/palette-widget.component';
import { DynamicSettings } from '../../../../component/wallpaper-widget/wallpaper-widget.component';
import { WALLPAPER_SIZES, WALLPAPER_STYLES, WallpaperService, WallpaperSize, WallpaperStyle, defaultParams, paramsAreDefault, parseParams } from '../../../../service/wallpaper/wallpaper.service';
import { DynamicWallpaperOptions, DynamicWallpaperService } from '../../../../service/wallpaper/dynamic/dynamic-wallpaper.service';
import { bandOrder, composeBands, distinctLooks } from '../../../../service/wallpaper/dynamic/day-bands';
import { DEFAULT_DAY_LIGHT, DYNAMIC_MODES, DynamicFrame, DynamicMode, FRAME_COUNTS, hourLabel, lightAndDark } from '../../../../service/wallpaper/dynamic/day-light';

/** the screens a Mac shows a desktop on: the wallpaper tool's desktop presets and the Apple displays */
const MAC_SIZES: WallpaperSize[] = [
  { key: '5k', label: '5120 × 2880', hint: '5K iMac / Studio Display', width: 5120, height: 2880 },
  { key: '6k', label: '6016 × 3384', hint: 'Pro Display XDR', width: 6016, height: 3384 },
  { key: 'imac', label: '4480 × 2520', hint: '24" iMac', width: 4480, height: 2520 },
  { key: 'mbp16', label: '3456 × 2234', hint: 'MacBook Pro 16"', width: 3456, height: 2234 },
  { key: 'mbp14', label: '3024 × 1964', hint: 'MacBook Pro 14"', width: 3024, height: 1964 },
  { key: 'air', label: '2560 × 1664', hint: 'MacBook Air 13"', width: 2560, height: 1664 },
  ...WALLPAPER_SIZES.filter( s => [ '4k', 'qhd', 'fhd', 'ultrawide', 'superultrawide' ].indexOf( s.key ) > -1 ),
];

/** the frames on the page are drawn this wide; the download draws them at the preset's size */
const FRAME_PREVIEW_W = 960;

/**
 * /tools/dynamic-wallpaper - the macOS dynamic wallpaper generator (2026-10-12). The wallpaper
 * generator's picture (the same palette builder, styles, fields and seed, all in the URL), drawn
 * for every hour of the day and saved as one .heic that macOS changes through the day: by the
 * clock, by the sun, or by Light and Dark mode. The frames and the file are made by
 * DynamicWallpaperService; nothing leaves the browser.
 */
@Component( {
  selector: 'app-dynamic-wallpaper-page',
  templateUrl: './dynamic-wallpaper-page.component.html',
  styleUrls: [
    './dynamic-wallpaper-page.component.scss',
    '../wallpaper-page/wallpaper-page.component.scss',
    '../tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
  ],
} )
export class DynamicWallpaperPageComponent implements OnInit, AfterViewInit, OnDestroy {

  URLS = URLS;
  SIZES = MAC_SIZES;
  MODES = DYNAMIC_MODES;
  FRAME_COUNTS = FRAME_COUNTS;

  // ----- the palette (path param) + its source (query params), as in the wallpaper tool
  palette: { value: string }[] = [];
  count = 6;
  private countSynced = false;
  scheme = '';
  foundTheme: any = null;
  initializer?: { colorName: string; value: string };
  fullSeed = '';
  uploadedImg?: { src: string; id: string };

  // ----- the picture
  style: WallpaperStyle = 'mesh';
  artSeed = 0;
  params: number[] = defaultParams( 'mesh' );

  // ----- the day
  mode: DynamicMode = 'solar';
  frameCount = 8;
  night = DEFAULT_DAY_LIGHT.night;
  glow = DEFAULT_DAY_LIGHT.glow;
  size: WallpaperSize = MAC_SIZES[ 0 ];

  /** every frame of the day, drawn small */
  frames: ( DynamicFrame & { url: string } )[] = [];
  /** each size card's own picture: every frame of the file in diagonal bands, at that screen's shape */
  sizeThumbs: { [ key: string ]: string } = {};
  /** the whole day as one picture (day-bands.ts): the hero, the section swatches, the widget's preview */
  dayUrl = '';
  /** the frames in band order (the size cards, the widget's style picker) */
  private dayOrder: DynamicFrame[] = [];
  /** the day is being drawn: the previews shade over with the Codigrate loader */
  framesLoading = true;
  private drawRun = 0;
  /** the hour on the preview stage, 0..24 */
  hour = 12;
  playing = false;
  private playTimer: any = null;
  private framesTimer: any = null;

  heroUrl = '';
  /** the build: '' idle, else the frame being coded */
  building = false;
  buildDone = 0;
  buildTotal = 0;
  buildError = '';
  buildErrorDetail = '';
  /** whether this browser can encode the chosen size (null until asked): the file needs its HEVC encoder */
  encoderSupported: boolean | null = null;
  private supportAsked = '';
  copied = false;
  private copiedTimer: any = null;

  pageSections: { key: string; label: string }[] = [
    { key: 'preview', label: 'Preview' },
    { key: 'download', label: 'Download' },
    { key: 'frames', label: 'Frames' },
    { key: 'install', label: 'Set It Up' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'dynamic-wallpaper';
  activeSection = 'top';
  readonly aboutLead = DYNAMIC_WALLPAPER_ABOUT.lead;
  readonly aboutParagraphs = DYNAMIC_WALLPAPER_ABOUT.paragraphs;
  readonly faq = DYNAMIC_WALLPAPER_ABOUT.faq;

  private sectionObserver?: IntersectionObserver;
  private editSubject = new Subject<void>();
  private editPending = false;
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private colorService: ColorService,
    private paletteGen: PaletteGenerationService,
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
    private clipboard: Clipboard,
    private wallpaperService: WallpaperService,
    private dynamicService: DynamicWallpaperService,
  ) {
  }

  ngOnInit(): void {
    this.setSeo();

    this.subs.push( this.route.params.subscribe( params => {
      if ( params.wallpaper ) {
        const hexes = String( params.wallpaper ).split( '-' );
        if ( hexes.length < 2 || hexes.length > 10 || hexes.some( h => !this.colorService.isValidHexCode( h ) ) ) {
          this.router.navigate( [ URLS.notFound ] );
          return;
        }
        const same = this.palette.length === hexes.length
          && this.palette.every( ( p, i ) => p.value.slice( 1 ).toLowerCase() === hexes[ i ].toLowerCase() );
        if ( !same ) {
          this.palette = hexes.map( h => ( { value: '#' + h.toLowerCase() } ) );
          if ( !this.countSynced ) {
            this.count = this.palette.length;
            this.countSynced = true;
          }
        }
        this.readParams( this.route.snapshot.queryParams );
        this.scheduleFrames();
      } else {
        // a blank visit: a random Codigrate theme palette and a random style, drawn in place at the bare URL
        const t = this.paletteGen.randomTheme();
        const hexes = t.hexes.length >= 2 ? t.hexes : [ '#00baff', '#ba00ff', '#0f172a' ];
        const qp: any = t.hexes.length >= 2 ? { theme: t.theme?.key } : {};
        const query = this.route.snapshot.queryParams;
        this.readParams( query );
        if ( !query[ 'style' ] ) {
          this.style = WALLPAPER_STYLES[ Math.floor( Math.random() * WALLPAPER_STYLES.length ) ].key;
          this.params = defaultParams( this.style );
        }
        this.count = hexes.length;
        this.countSynced = true;
        if ( this.palette.length === 0 ) {
          this.palette = hexes.map( h => ( { value: '#' + h.replace( '#', '' ).toLowerCase() } ) );
          this.applySourceParams( qp );
          this.scheduleFrames();
        }
        this.navigateTo( hexes, qp, true, true );
      }
    } ) );

    this.subs.push( this.route.queryParams.subscribe( params => {
      if ( !this.route.snapshot.params.wallpaper ) {
        return;
      }
      this.applySourceParams( params );
      this.readParams( params );
      if ( params.theme || params.scheme ) {
        this.uploadedImg = undefined;
      }
      this.scheduleFrames();
    } ) );

    this.subs.push( this.editSubject.pipe( debounceTime( 600 ) ).subscribe( () => {
      if ( !this.editPending ) {
        return;
      }
      this.editPending = false;
      this.navigateTo( this.palette.map( p => p.value ), {} );
    } ) );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.sectionObserver = new IntersectionObserver( entries => {
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
          }
        }
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.subs.forEach( s => s.unsubscribe() );
    this.sectionObserver?.disconnect();
    this.stop();
    if ( this.framesTimer ) clearTimeout( this.framesTimer );
    if ( this.copiedTimer ) clearTimeout( this.copiedTimer );
  }

  // ---------------------------------------------------------------- URL

  /** the picture and the day from the URL; a missing art seed gets a fresh one */
  private readParams( params: any ): void {
    this.style = this.wallpaperService.styleOf( params.style );
    const art = parseInt( params.art, 10 );
    this.artSeed = !isNaN( art ) && art > 0 ? art : ( this.artSeed || this.wallpaperService.randomSeed() );
    this.params = parseParams( params.p, this.style );
    this.mode = DYNAMIC_MODES.find( m => m.key === params.mode )?.key || 'solar';
    const frames = parseInt( params.frames, 10 );
    this.frameCount = FRAME_COUNTS.indexOf( frames ) > -1 ? frames : 8;
    this.night = this.share( params.night, DEFAULT_DAY_LIGHT.night );
    this.glow = this.share( params.glow, DEFAULT_DAY_LIGHT.glow );
    this.size = MAC_SIZES.find( s => s.key === params.size ) || MAC_SIZES[ 0 ];
  }

  private share( raw: any, fallback: number ): number {
    const n = parseInt( raw, 10 );
    return isNaN( n ) ? fallback : Math.max( 0, Math.min( 100, n ) );
  }

  /** every key is set (null drops it): the navigation merges query params, so an omitted key would keep a stale value */
  private pictureQuery(): any {
    return {
      style: this.style,
      art: this.artSeed,
      p: paramsAreDefault( this.style, this.params ) ? null : this.params.join( '-' ),
      mode: this.mode !== 'solar' ? this.mode : null,
      frames: this.frameCount !== 8 ? this.frameCount : null,
      night: this.night !== DEFAULT_DAY_LIGHT.night ? this.night : null,
      glow: this.glow !== DEFAULT_DAY_LIGHT.glow ? this.glow : null,
      size: this.size.key !== MAC_SIZES[ 0 ].key ? this.size.key : null,
    };
  }

  private persist(): void {
    this.scheduleFrames();
    this.router.navigate( [], {
      relativeTo: this.route,
      queryParams: this.pictureQuery(),
      queryParamsHandling: 'merge',
      replaceUrl: true,
      state: { scrollToTop: false },
    } );
  }

  private navigateTo( hexes: string[], queryParams: any, replace = false, skipLocation = false ): void {
    this.editPending = false;
    const params = hexes.map( h => h.replace( '#', '' ) ).join( '-' );
    this.router.navigate( [ URLS.tools.dynamicWallpaper + '/' + params ],
      { queryParams: { ...queryParams, ...this.pictureQuery() }, replaceUrl: replace, skipLocationChange: skipLocation, state: { scrollToTop: false } } );
  }

  // ---------------------------------------------------------------- the day

  private options(): DynamicWallpaperOptions {
    return {
      style: this.style, colors: this.palette.map( p => p.value ), seed: this.artSeed, params: this.params,
      mode: this.mode, frames: this.frameCount, light: { night: this.night, glow: this.glow },
    };
  }

  /** every frame redrawn small after a burst of changes */
  private scheduleFrames(): void {
    if ( this.framesTimer ) clearTimeout( this.framesTimer );
    // the previews shade over at once, and stay so until the new day is drawn
    this.framesLoading = true;
    this.framesTimer = setTimeout( () => {
      this.framesTimer = null;
      this.drawFrames();
    }, 60 );
  }

  /** a breath for the page between two pictures: the loader keeps moving while the day is drawn */
  private breathe(): Promise<void> {
    return new Promise( resolve => setTimeout( resolve ) );
  }

  /**
   * Every frame, the day picture and the size cards, one picture at a time so the page stays alive; a
   * newer change stops an older drawing (the previews keep the last day until the new one is ready).
   */
  private async drawFrames(): Promise<void> {
    const run = ++this.drawRun;
    if ( !this.palette.length || typeof document === 'undefined' ) {
      this.frames = [];
      this.framesLoading = false;
      return;
    }
    const options = this.options();
    const w = FRAME_PREVIEW_W, h = Math.round( FRAME_PREVIEW_W * 9 / 16 );
    // the picture once; each frame is its pixels with that hour's light: the frame cards take its picture,
    // the day picture its band
    const drawn = new Map<number, Uint8ClampedArray>();
    const frames: ( DynamicFrame & { url: string } )[] = [];
    const baseCanvas = document.createElement( 'canvas' );
    baseCanvas.width = w;
    baseCanvas.height = h;
    let base: ImageData | null = null;
    try {
      this.dynamicService.renderBase( baseCanvas, options );
      base = baseCanvas.getContext( '2d' )?.getImageData( 0, 0, w, h ) || null;
    } catch {
      base = null;
    }
    for ( const frame of this.dynamicService.frames( options ) ) {
      await this.breathe();
      if ( run !== this.drawRun ) return;
      let url = '';
      try {
        if ( !base ) throw new Error( 'no picture' );
        const pixels = new Uint8ClampedArray( base.data );
        this.dynamicService.relightPixels( pixels, options, frame );
        url = this.pixelsUrl( pixels, w, h, 0.85 );
        drawn.set( frame.index, pixels );
      } catch {
        url = '';
      }
      frames.push( { ...frame, url } );
    }
    // one equal band for each look the day has (frames that look the same share one), brightest to darkest
    const order = distinctLooks( bandOrder( frames ) );
    // the day as one picture: the hero, the section swatches, the widget's preview
    let dayUrl = '';
    if ( order.length && order.every( f => drawn.has( f.index ) ) ) {
      try {
        dayUrl = this.pixelsUrl( composeBands( w, h, order.map( f => drawn.get( f.index )! ) ), w, h, 0.88 );
      } catch {
        dayUrl = '';
      }
    }
    // each size card: the whole day in bands at that screen's shape, twice the card's size for Retina
    const thumbs: { [ key: string ]: string } = {};
    const card = document.createElement( 'canvas' );
    for ( const sz of MAC_SIZES ) {
      await this.breathe();
      if ( run !== this.drawRun ) return;
      const scale = 336 / Math.max( sz.width, sz.height );
      card.width = Math.max( 24, Math.round( sz.width * scale ) );
      card.height = Math.max( 24, Math.round( sz.height * scale ) );
      thumbs[ sz.key ] = this.dayPicture( card, options, order );
    }
    this.frames = frames;
    this.dayUrl = dayUrl;
    this.dayOrder = order;
    this.sizeThumbs = thumbs;
    this.framesLoading = false;
    this.checkEncoder();
    if ( this.mode === 'appearance' && this.hour !== 12 && this.hour !== 0 ) {
      this.hour = 12;
    }
  }

  /** RGBA pixels as a JPEG data url */
  private pixelsUrl( pixels: Uint8ClampedArray, w: number, h: number, quality: number ): string {
    const canvas = document.createElement( 'canvas' );
    canvas.width = w;
    canvas.height = h;
    canvas.getContext( '2d' )!.putImageData( new ImageData( pixels, w, h ), 0, 0 );
    return canvas.toDataURL( 'image/jpeg', quality );
  }

  /** the whole day in bands at `canvas`'s size: the picture drawn once, each band its pixels with its hour's light */
  private paintDay( canvas: HTMLCanvasElement, options: DynamicWallpaperOptions, order: DynamicFrame[] ): Uint8ClampedArray {
    const w = canvas.width, h = canvas.height;
    this.dynamicService.renderBase( canvas, options );
    const base = canvas.getContext( '2d' )!.getImageData( 0, 0, w, h ).data;
    return composeBands( w, h, order.map( f => {
      const pixels = new Uint8ClampedArray( base );
      this.dynamicService.relightPixels( pixels, options, f );
      return pixels;
    } ) );
  }

  private dayPicture( canvas: HTMLCanvasElement, options: DynamicWallpaperOptions, order: DynamicFrame[] ): string {
    try {
      return this.pixelsUrl( this.paintDay( canvas, options, order ), canvas.width, canvas.height, 0.85 );
    } catch {
      return '';
    }
  }


  /** the backdrop and the swatches: the day picture, or the widget's picture until the day is drawn */
  get heroImage(): string {
    return this.dayUrl || this.heroUrl;
  }

  /** the frames in the order of the day, for the stage */
  private get byHour(): ( DynamicFrame & { url: string } )[] {
    return [ ...this.frames ].sort( ( a, b ) => a.hour - b.hour );
  }

  /**
   * The stage at `hour`: the frame shown until the next one, fading into it over the last stretch,
   * the way the desktop eases from one picture to the next.
   */
  get stage(): { from: string; to: string; mix: number; frame: DynamicFrame | null } {
    const list = this.byHour;
    if ( !list.length ) return { from: '', to: '', mix: 0, frame: null };
    if ( this.mode === 'appearance' ) {
      const f = this.isDark ? list[ 0 ] : list[ list.length - 1 ];
      return { from: f.url, to: f.url, mix: 0, frame: f };
    }
    let i = list.length - 1;
    for ( let k = 0; k < list.length; k++ ) {
      if ( list[ k ].hour <= this.hour ) i = k;
    }
    const from = list[ i ];
    const next = list[ ( i + 1 ) % list.length ];
    let span = next.hour - from.hour;
    if ( span <= 0 ) span += 24;
    let into = this.hour - from.hour;
    if ( into < 0 ) into += 24;
    // the change happens in the last 40% of the stretch, a soft cross fade
    const t = Math.max( 0, Math.min( 1, ( into / span - 0.6 ) / 0.4 ) );
    return { from: from.url, to: next.url, mix: t * t * ( 3 - 2 * t ), frame: from };
  }

  /** Light & Dark: the stage follows a Light / Dark switch instead of the clock */
  get isDark(): boolean {
    return this.hour < 6 || this.hour >= 18;
  }

  get hourText(): string {
    return hourLabel( this.hour );
  }

  get lightDark(): { light: number; dark: number } {
    return lightAndDark( this.frames );
  }

  get modeInfo() {
    return DYNAMIC_MODES.find( m => m.key === this.mode ) || DYNAMIC_MODES[ 0 ];
  }

  get styleInfo() {
    return WALLPAPER_STYLES.find( s => s.key === this.style ) || WALLPAPER_STYLES[ 0 ];
  }

  setHour( value: number | string ): void {
    const h = typeof value === 'number' ? value : parseFloat( value );
    this.hour = isNaN( h ) ? 12 : Math.max( 0, Math.min( 23.99, h ) );
  }

  /** a frame card puts the stage at its hour */
  showFrame( frame: DynamicFrame ): void {
    this.stop();
    this.hour = frame.hour;
    this.scrollToSection( 'preview' );
  }

  setAppearance( dark: boolean ): void {
    this.stop();
    this.hour = dark ? 0 : 12;
  }

  /** the day played in twelve seconds */
  togglePlay(): void {
    if ( this.playing ) {
      this.stop();
      return;
    }
    this.playing = true;
    this.analyticsService.logEvent( 'Dynamic Wallpaper Played', { mode: this.mode } );
    this.playTimer = setInterval( () => {
      this.hour = ( this.hour + 0.1 ) % 24;
    }, 50 );
  }

  private stop(): void {
    this.playing = false;
    if ( this.playTimer ) clearInterval( this.playTimer );
    this.playTimer = null;
  }

  /** the day's settings, as the widget shows them */
  get dynamicSettings(): DynamicSettings {
    const key = `${ this.mode }|${ this.frameCount }|${ this.night }|${ this.glow }`;
    if ( key !== this.settingsKey ) {
      this.settingsKey = key;
      this.settings = { mode: this.mode, frames: this.frameCount, night: this.night, glow: this.glow };
    }
    return this.settings;
  }
  private settingsKey = '';
  private settings: DynamicSettings = { mode: 'solar', frames: 8, night: DEFAULT_DAY_LIGHT.night, glow: DEFAULT_DAY_LIGHT.glow };

  onDynamicChange( change: Partial<DynamicSettings> ): void {
    if ( change.mode ) {
      this.onModeChange( DYNAMIC_MODES.find( m => m.key === change.mode )?.key || 'solar' );
    }
    if ( change.frames !== undefined ) {
      this.onFrameCountChange( change.frames );
    }
    if ( change.night !== undefined || change.glow !== undefined ) {
      if ( change.night !== undefined ) this.night = this.share( change.night, DEFAULT_DAY_LIGHT.night );
      if ( change.glow !== undefined ) this.glow = this.share( change.glow, DEFAULT_DAY_LIGHT.glow );
      this.persist();
    }
  }

  onModeChange( mode: DynamicMode ): void {
    if ( mode === this.mode ) return;
    this.mode = mode;
    this.stop();
    this.persist();
    this.analyticsService.logEvent( 'Dynamic Wallpaper Mode Changed', { mode } );
  }

  onFrameCountChange( count: number ): void {
    if ( count === this.frameCount ) return;
    this.frameCount = count;
    this.persist();
  }

  selectSize( size: WallpaperSize ): void {
    this.size = size;
    this.persist();
    this.checkEncoder();
  }

  /** asks the browser once per size whether it can HEVC encode it */
  private checkEncoder(): void {
    const key = this.size.key;
    if ( this.supportAsked === key || typeof window === 'undefined' ) return;
    this.supportAsked = key;
    this.encoderSupported = null;
    this.dynamicService.supports( this.size ).then( ok => {
      if ( this.size.key === key ) {
        this.zone.run( () => this.encoderSupported = ok );
      }
    }, () => this.zone.run( () => this.encoderSupported = false ) );
  }

  onStyleChange( value: string ): void {
    this.style = this.wallpaperService.styleOf( value );
    this.params = defaultParams( this.style );
    this.persist();
    this.analyticsService.logEvent( 'Dynamic Wallpaper Style Changed', { style: this.style } );
  }

  onParamsChange( params: number[] ): void {
    this.params = params;
    this.persist();
  }

  // ---------------------------------------------------------------- the file

  async download(): Promise<void> {
    if ( this.building || !this.palette.length ) return;
    this.stop();
    this.building = true;
    this.buildError = '';
    this.buildErrorDetail = '';
    this.buildDone = 0;
    this.buildTotal = this.frames.length;
    const size = this.size;
    // let the button repaint before the first frame is drawn at full size
    await new Promise( r => setTimeout( r, 40 ) );
    try {
      const blob = await this.dynamicService.build( this.options(), size, ( done, total ) => this.zone.run( () => {
        this.buildDone = done;
        this.buildTotal = total;
      } ) );
      const a = document.createElement( 'a' );
      const url = URL.createObjectURL( blob );
      a.href = url;
      a.download = this.fileName( size );
      document.body.appendChild( a );
      a.click();
      a.remove();
      setTimeout( () => URL.revokeObjectURL( url ), 10000 );
      this.analyticsService.logEvent( 'Dynamic Wallpaper Downloaded', {
        style: this.style, mode: this.mode, frames: this.frames.length, size: size.key, width: size.width, height: size.height,
        theme: this.foundTheme?.key || null, source: this.currentGenerator || 'custom', bytes: blob.size,
      } );
    } catch ( e: any ) {
      console.error( 'Dynamic wallpaper build failed:', e );
      this.buildError = 'The wallpaper could not be made in this browser. Try a smaller size, or Safari or Chrome on a Mac.';
      // what the browser said, small under it: the encoder's own words are what a fix needs
      this.buildErrorDetail = String( e?.message || e || '' ).slice( 0, 400 );
      this.analyticsService.logEvent( 'Dynamic Wallpaper Failed', {
        style: this.style, mode: this.mode, frames: this.frames.length, size: size.key, width: size.width, height: size.height,
        error: this.buildErrorDetail, userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      } );
    } finally {
      this.zone.run( () => this.building = false );
    }
  }

  get buildPercent(): number {
    return this.buildTotal ? Math.round( this.buildDone / this.buildTotal * 100 ) : 0;
  }

  private fileName( size: WallpaperSize ): string {
    const who = this.foundTheme?.key ? `${ this.foundTheme.key }-` : '';
    return `codigrate-${ who }${ this.style }-dynamic-wallpaper-${ size.width }x${ size.height }.heic`;
  }

  copyLink(): void {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if ( !url ) return;
    this.clipboard.copy( url );
    this.copied = true;
    if ( this.copiedTimer ) clearTimeout( this.copiedTimer );
    this.copiedTimer = setTimeout( () => this.copied = false, 1500 );
    this.analyticsService.logEvent( 'Dynamic Wallpaper Link Copied', { style: this.style } );
  }

  // ---------------------------------------------------------------- palette (as the wallpaper tool)

  private applySourceParams( params: any ): void {
    this.foundTheme = params.theme
      ? THEMES.map( l => l.products ).reduce( ( a: any[], b: any[] ) => a.concat( b ), [] ).find( ( p: any ) => p.key === params.theme )
      : null;
    this.initializer = params.initializer
      ? {
        colorName: String( params.initializer ).replace( /-/g, ' ' ),
        value: HEX_CODES[ params.initializer ] ? '#' + HEX_CODES[ params.initializer ] : '',
      }
      : undefined;
    this.scheme = params.scheme || '';
    this.fullSeed = params.seed || '';
  }

  private startQueryParams( start: { theme?: any; scheme?: string; initializer?: string; seed?: string } ): any {
    if ( start.theme ) {
      return { theme: start.theme.key };
    }
    const qp: any = { scheme: start.scheme };
    if ( start.initializer ) { qp.initializer = start.initializer; }
    if ( start.seed ) { qp.seed = start.seed; }
    return qp;
  }

  onGenerate( scheme: string ): void {
    this.artSeed = this.wallpaperService.randomSeed();
    if ( scheme === 'theme' || scheme.startsWith( 'theme:' ) ) {
      const t = scheme.startsWith( 'theme:' ) ? this.paletteGen.themeByKey( scheme.slice( 6 ) ) : this.paletteGen.randomTheme( this.foundTheme?.key );
      if ( !t.hexes.length ) {
        return;
      }
      this.navigateTo( t.hexes.slice( 0, this.count ), { theme: t.theme?.key } );
      this.analyticsService.logEvent( 'Dynamic Wallpaper Initialized', { source: 'theme' } );
      return;
    }
    const r = this.paletteGen.build( scheme, this.count );
    if ( !r.hexes.length ) {
      return;
    }
    this.navigateTo( r.hexes, this.startQueryParams( r ) );
    this.analyticsService.logEvent( 'Dynamic Wallpaper Initialized', { source: r.scheme || scheme } );
  }

  onEditColor( change: { index: number; value: string } ): void {
    const item = this.palette[ change.index ];
    if ( !item ) {
      return;
    }
    item.value = change.value;
    this.scheduleFrames();
    this.editPending = true;
    this.editSubject.next();
  }

  drop( event: CdkDragDrop<unknown> ): void {
    moveItemInArray( this.palette, event.previousIndex, event.currentIndex );
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  /** the widget's reverse button: the palette in the opposite order, the same style, seed and fields */
  reverseColors(): void {
    if ( this.palette.length < 2 ) return;
    this.palette.reverse();
    this.navigateTo( this.palette.map( p => p.value ), {} );
    this.analyticsService.logEvent( 'Dynamic Wallpaper Reversed', { count: this.palette.length } );
  }

  addColor(): void {
    if ( this.palette.length >= 10 ) {
      return;
    }
    this.palette.push( { value: this.colorService.generateRandomHexColor() } );
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  removeColor( index: number ): void {
    if ( this.palette.length <= 2 ) {
      return;
    }
    this.palette.splice( index, 1 );
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  handleImageUpload( event: any ): void {
    const file = event.target.files[ 0 ];
    if ( !file ) {
      return;
    }
    const reader = new FileReader();
    reader.onload = ( e: any ) => {
      const img = new Image();
      img.src = e.target.result;
      img.id = file.name;
      img.onload = async () => {
        try {
          let colors = await extractColors( img.src, {
            pixels: 500, distance: 0.1, saturationDistance: 0, lightnessDistance: 0, hueDistance: 0,
          } );
          this.zone.run( () => {
            colors.sort( ( a, b ) => b.area - a.area );
            if ( colors.length > this.count ) {
              colors = colors.slice( 0, this.count );
            }
            while ( colors.length < 2 ) {
              colors.push( colors[ 0 ] );
            }
            const palette = colors.map( c => ( { value: c.hex } ) );
            this.colorService.sortByHue( palette );
            const canvas = document.createElement( 'canvas' );
            const scale = Math.min( 1, 640 / img.width );
            canvas.width = Math.round( img.width * scale );
            canvas.height = Math.round( img.height * scale );
            const ctx = canvas.getContext( '2d' );
            if ( ctx ) {
              ctx.drawImage( img, 0, 0, canvas.width, canvas.height );
              this.uploadedImg = { src: canvas.toDataURL( 'image/jpeg', 0.85 ), id: file.name };
            }
            this.artSeed = this.wallpaperService.randomSeed();
            this.navigateTo( palette.map( p => p.value ), {} );
            this.analyticsService.logEvent( 'Dynamic Wallpaper Initialized', { source: 'image' } );
          } );
        } catch ( error ) {
          console.error( 'Error reading image palette:', error );
        }
      };
    };
    reader.readAsDataURL( file );
  }

  // ---------------------------------------------------------------- subtitle

  private themeHref( theme: any ): string {
    for ( const line of THEMES ) {
      if ( line.products.some( ( p: any ) => p.key === theme?.key ) ) {
        return `${ URLS.theme }?product=${ theme.key }&category=${ ( line.name || '' ).toLowerCase() }`;
      }
    }
    return '';
  }

  private colorHref( hex: string ): string {
    const h = ( hex || '' ).replace( '#', '' );
    return h ? `${ URLS.tools.color }/${ h }` : '';
  }

  private get themeName(): string {
    const name = ( this.foundTheme?.name || '' ).trim().replace( /\s*theme\s*$/i, '' );
    return name ? `${ name } Theme` : '';
  }

  get currentGenerator(): string {
    if ( this.uploadedImg ) { return 'image'; }
    if ( this.foundTheme ) { return 'theme'; }
    return this.scheme || '';
  }

  get subtitle(): PaletteSubtitle {
    if ( this.uploadedImg ) {
      return { kind: 'image', imageSrc: this.uploadedImg.src, imageName: this.uploadedImg.id };
    }
    if ( this.foundTheme ) {
      return { kind: 'theme', themeName: this.themeName, logo: this.foundTheme.logo, themeHref: this.themeHref( this.foundTheme ) };
    }
    const sourced = [ 'complementary', 'split complementary', 'triadic', 'tetradic', 'analogous', 'monochrome', 'warm', 'cool' ];
    if ( sourced.includes( this.scheme ) && this.initializer ) {
      const name = ( this.initializer.colorName || '' ).replace( /\b\w/g, c => c.toUpperCase() );
      return { kind: 'harmony', scheme: this.scheme, name, color: this.initializer.value, colorHref: this.colorHref( this.initializer.value ) };
    }
    if ( this.scheme === 'full' ) {
      return { kind: 'full', seed: this.fullSeed || '' };
    }
    return { kind: 'custom', seed: this.palette.length ? this.paletteGen.paletteSeed( this.palette.map( p => p.value ) ) : '' };
  }

  /** the source as plain text, under the title and over every section */
  get sourceText(): string {
    const s = this.subtitle;
    switch ( s.kind ) {
      case 'theme': return s.themeName || 'Theme';
      case 'harmony': return s.name || '';
      case 'image': return s.imageName || 'Uploaded Image';
      case 'full': return s.seed ? 'seed-' + s.seed : 'Random';
      default: return s.seed ? 'seed-' + s.seed : 'Custom';
    }
  }

  get sourceLabel(): string {
    return this.styleInfo.label + ' · ' + this.modeInfo.label + ' · ' + this.sourceText;
  }

  /** the page's top band: the palette's colours side by side, fading down to the page (as the wallpaper tool) */
  @HostBinding( 'style.--band-row' ) get bandRow(): string {
    const colors = this.palette.map( p => p.value ).filter( c => /^#[0-9a-f]{6}$/i.test( c || '' ) );
    if ( colors.length < 2 ) {
      return 'linear-gradient(180deg, var(--theme-color) 0%, var(--background-color) 100%)';
    }
    const stops = colors.map( c => `color-mix(in oklab, ${ c } 55%, var(--background-color))` );
    return `linear-gradient(90deg, ${ stops.join( ', ' ) })`;
  }

  // ---------------------------------------------------------------- seo

  private setSeo(): void {
    this.seoService.setMetaData( {
      title: 'Free macOS Dynamic Wallpaper Generator - Download HEIC | Codigrate',
      description: 'Free macOS dynamic wallpaper generator by Codigrate. Turn any color palette into a wallpaper lit for every hour, from sunrise to night, and download one .heic that changes with the time of day, the sun position or Light and Dark mode, for 5K, 6K, iMac and MacBook.',
      keywords: [ 'dynamic wallpaper', 'macos dynamic wallpaper', 'dynamic wallpaper generator', 'heic wallpaper', 'dynamic desktop', 'mac wallpaper', 'light and dark wallpaper', 'time of day wallpaper', 'codigrate' ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  // ---------------------------------------------------------------- page

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.tweenScroll( scroller, 0 );
      return;
    }
    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    // clear the nav and the sticky chips' bar under it
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }
}
